import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { endOfMonth, isSameDay, isSameMonth } from 'date-fns';
import { addMonths } from 'date-fns/addMonths';
import { addYears } from 'date-fns/addYears';
import { format } from 'date-fns/format';
import { startOfMonth } from 'date-fns/startOfMonth';
import React, {
    Fragment,
    forwardRef,
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { cn } from '@/lib/css-utils';

import { BaseUIComponentProps, defaultBaseUIComponentProps } from './base';
import { DatePicker } from './date-picker';
import { useOutsideClick } from './hooks/use-outside-click';
import { Paragraph } from './paragraph';
import { useGlobalDefaults } from './provider';
import { Switch } from './switch';

export type DatePeriod = {
    start: Date | null;
    end: Date | null;
};

export const isDatePeriod = (value: any): value is DatePeriod => {
    return typeof value === 'object' && value !== null && 'start' in value && 'end' in value;
};

const midDay = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(12, 0, 0, 0);
    return newDate;
};

const useSliderMonths = (start: Date, end: Date) => {
    return useMemo(() => {
        const months = [];

        let current = startOfMonth(midDay(start));

        while (current <= end) {
            months.push({
                value: new Date(current.getTime()),
                label: format(current, 'MMM yy'),
            });

            current = addMonths(current, 1);
        }

        return months;
    }, [start, end]);
};

const indicatorVariants = cva(
    classNames(
        'border border-token-stroke-button-default h-10 w-6',
        'group-hover/slider-button:border-token-stroke-input-hover',
        'group-focus/slider-button:border-token-stroke-input-hover'
    ),
    {
        variants: {
            type: {
                left: 'rounded-l-lg',
                right: 'rounded-r-lg',
            },
            isDisabled: {
                true: 'border-token-stroke-button-disabled',
                false: '',
            },
            isActive: {
                true: 'bg-token-fill-button-primary-focused border-token-stroke-button-default group-hover/slider-button:border-token-stroke-button-default',
                false: '',
            },
        },
    }
);

const isInRange = (range: number[] | null, index: number): boolean => {
    if (!range || range.length === 0) {
        return false;
    }

    const [start, end] = range;

    return index >= start && index <= end;
};

const getMonthIndexFromXPos = (xPos: number) => {
    // One slice is 24px wide, followed by 8px full month button and another 24px slice
    //  after this we have a 16px space
    //
    // We want to know which slice index we are on
    //
    // | 24 px slice | 8 px full month button | 24 px slice | 16 px space |  24 px slice | 8 px full month button | 24 px slice | ... |

    const sliceWidth = 24;
    const gapWidth = 8;
    const spacerWidth = 16;
    const patternWidth = sliceWidth + gapWidth + sliceWidth + spacerWidth; // 72px

    if (xPos < 0) {
        return 0;
    }

    const patternIndex = Math.floor(xPos / patternWidth);
    const localX = xPos % patternWidth;

    const baseIndex = patternIndex * 3;

    if (localX < sliceWidth) {
        return baseIndex;
    }

    if (localX < sliceWidth + gapWidth) {
        return baseIndex + 1;
    }

    if (localX < sliceWidth + gapWidth + sliceWidth) {
        return baseIndex + 2;
    }

    // Spacer (no element), return previous real element
    return baseIndex + 2;
};

const getCursorPosX = (e: React.MouseEvent | React.TouchEvent) => {
    return (e as React.MouseEvent).clientX ?? (e as React.TouchEvent).touches[0].clientX;
};

const getMiddleOfMonth = (monthStart: Date, monthEnd: Date) => {
    const monthMiddle = monthEnd.getTime() - monthStart.getTime();

    const monthMiddleDate = midDay(new Date(monthStart.getTime() + monthMiddle / 2));

    return monthMiddleDate;
};

const scrollOffset = 56 + 16;

interface SliderSelectorRef {
    handleOutsideClick?: () => void;
}

const PeriodSliderSelector = forwardRef<
    SliderSelectorRef,
    {
        months: { value: Date; label: string; isDisabled?: boolean }[];

        value: DatePeriod | null;

        isLoading?: boolean;
        disabled?: boolean;

        rootRef: React.RefObject<HTMLDivElement | null>;

        setValue: (value: DatePeriod | null) => void;
        mode?: 'light' | 'dark';
    }
>(
    (
        {
            months,
            value,
            setValue,

            mode,
            isLoading,
            disabled,
            rootRef,
        },
        ref
    ) => {
        const parentRef = useRef<HTMLDivElement>(null);

        const [canScrollLeft, setCanScrollLeft] = useState(false);
        const [canScrollRight, setCanScrollRight] = useState(false);

        const onScroll = useCallback(() => {
            if (!parentRef.current) {
                return;
            }

            setCanScrollLeft(parentRef.current.scrollLeft > 0);
            setCanScrollRight(
                parentRef.current.scrollLeft + parentRef.current.clientWidth < parentRef.current.scrollWidth
            );
        }, []);

        const resizeObserver = useRef<ResizeObserver | null>(null);

        useLayoutEffect(() => {
            if (!parentRef.current) {
                return;
            }

            resizeObserver.current = new ResizeObserver(() => {
                onScroll();
            });

            resizeObserver.current.observe(parentRef.current);

            return () => {
                resizeObserver.current?.disconnect();
            };
        }, [onScroll]);

        const scrollToOffset = useCallback((offset: number) => {
            if (parentRef.current) {
                const newX = parentRef.current.scrollLeft + offset;

                parentRef.current.scrollTo({
                    left: newX >= 0 ? newX : 0,
                    behavior: 'smooth',
                });
            }
        }, []);

        const startVal = value?.start ?? null;

        const firstScrollHandled = useRef(false);
        useLayoutEffect(() => {
            if (!firstScrollHandled.current) {
                // If theres an initial value, scroll it into view
                if (startVal) {
                    const startMonth = startOfMonth(startVal);
                    scrollToOffset(
                        // 72px is the width of one month block (24px slice + 8px full month button + 24px slice + 16px space)
                        months.findIndex((month) => isSameMonth(month.value, startMonth)) * 72
                    );
                }

                onScroll();

                firstScrollHandled.current = true;
            }
        }, [onScroll, scrollToOffset, startVal]);

        const highlightStartIndex = useRef<number | null>(null);
        const [highlightedRange, setHighlightedRange] = useState<number[] | null>(null);

        const [hoveredMonth, setHoveredMonth] = useState<Date | null>(null);

        const createOnClickHandler = useMemo(() => {
            const onClickA =
                (startRangeDate: Date, endRangeDate: Date, stopPropagation: boolean = false, idx: number) =>
                (e: React.MouseEvent) => {
                    if (stopPropagation) {
                        e.stopPropagation();
                    }

                    highlightStartIndex.current = idx;

                    // We have only start selected, make a new range with the end date
                    if (value && value.start && !value.end) {
                        // edge case, end date is before start date. treat it as a new start
                        if (startRangeDate < value.start) {
                            setValue({
                                start: startRangeDate,
                                end: null,
                            });
                            setHighlightedRange(null);
                            return;
                        }

                        // If the end date is after start date, make a new range
                        setValue({
                            start: value.start,
                            end: endRangeDate,
                        });
                        setHighlightedRange(null);
                        return;
                    }

                    if (!value || (!value.start && !value.end)) {
                        // If we have no value, set the start and end to the clicked date
                        setValue({
                            start: startRangeDate,
                            end: null,
                        });
                        setHighlightedRange(null);
                        return;
                    }

                    if (value && value.start && value.end) {
                        // user had a range selected but clicked on a new month. start a new range
                        setValue({
                            start: startRangeDate,
                            end: null,
                        });
                        setHighlightedRange(null);
                        return;
                    }

                    setValue({
                        start: startRangeDate,
                        end: null,
                    });
                    setHighlightedRange(null);
                };

            return onClickA;
        }, [value, value?.start, value?.end, setValue, setHighlightedRange]);

        const onKeyDown = (e: React.KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowLeft':
                    focusMonth(parentRef, -1);
                    break;

                case 'ArrowRight':
                    focusMonth(parentRef, 1);
                    break;

                case 'PageDown':
                    focusMonth(parentRef, 4);
                    break;

                case 'PageUp':
                    focusMonth(parentRef, -4);
                    break;

                case 'End':
                    focusMonth(parentRef, 'end');
                    break;

                case 'Home':
                    focusMonth(parentRef, 'start');
                    break;

                default:
                    break;
            }
        };

        const onMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
            if (!parentRef.current) {
                return;
            }

            const wrapper = parentRef.current.children[0];

            if (!wrapper) {
                return;
            }

            // If there is something selected (e.g. range start)
            const start = value?.start;

            if (!start) {
                setHighlightedRange(null);
                return;
            }

            // If we have both and end we dont need to deal with dragged range
            if (value?.end) {
                setHighlightedRange(null);
                return;
            }

            const xPos = getCursorPosX(e);

            // Update dragged range
            const xPosInWrapper = xPos - wrapper.getBoundingClientRect().left;

            const targetIndex = getMonthIndexFromXPos(xPosInWrapper);

            const curStartIndex = highlightStartIndex.current;

            // if we have a start and we moved over to an item before, ignore dragged range update
            if (curStartIndex !== null && targetIndex < curStartIndex) {
                setHighlightedRange([curStartIndex, curStartIndex]);
                return;
            }

            const newRange = [curStartIndex ?? targetIndex, targetIndex];
            newRange.sort((a, b) => a - b);

            if (targetIndex === newRange[0] && targetIndex === newRange[1]) {
                // if the target is the same as the current range, we don't need to update it
                return;
            }

            setHighlightedRange(newRange);
        };

        const handleFullBlur = useCallback(() => {
            // If we only have a start date when user blurs the slider, lets just make it into a range
            if (value && value.start && !value.end) {
                let end =
                    value.start.getDate() === 1
                        ? getMiddleOfMonth(startOfMonth(value.start), endOfMonth(value.start))
                        : endOfMonth(value.start);

                if (hoveredMonth && isSameMonth(hoveredMonth, value.start)) {
                    end = endOfMonth(hoveredMonth);
                }

                setValue({
                    start: value.start,
                    end,
                });
            }
        }, [setValue, value]);

        const isDragging = useRef(false);
        const baseMouseX = useRef<number | null>(null);

        const onTouchStart = useCallback((e: React.TouchEvent) => {
            if (!parentRef.current) {
                return;
            }
            const wrapper = parentRef.current.children[0];
            if (!wrapper) {
                return;
            }

            isDragging.current = true;
            baseMouseX.current = getCursorPosX(e);
        }, []);

        const onTouchEnd = useCallback(() => {
            isDragging.current = false;
            baseMouseX.current = null;
        }, []);

        const onTouchMove = useCallback((e: React.TouchEvent) => {
            const curPos = getCursorPosX(e);

            const delta = (curPos - (baseMouseX.current ?? 0)) * -1;

            // scroll the parent element if we are dragging
            if (isDragging.current && parentRef.current) {
                parentRef.current.scrollLeft += delta;
                baseMouseX.current = curPos;
            }
        }, []);

        useImperativeHandle(ref, () => ({
            handleOutsideClick: handleFullBlur,
        }));

        return (
            <div
                className={classNames('flex gap-2 mb-px pb-px relative touch-none', {
                    'animate-pulse': isLoading,
                    'opacity-50': disabled && !isLoading,
                })}
                onMouseLeave={() => {
                    setHighlightedRange(null);
                }}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                onTouchMove={onTouchMove}
            >
                <div className="flex items-center justify-center pb-5">
                    <button
                        type="button"
                        disabled={!canScrollLeft}
                        className={classNames({
                            'cursor-pointer': canScrollLeft,
                            'opacity-50': !canScrollLeft,
                        })}
                        onClick={() => scrollToOffset(-scrollOffset)}
                    >
                        <IconCaretLeftFilled size={16} />
                    </button>
                </div>

                <div
                    className="flex items-center justify-between overflow-x-hidden pb-5"
                    ref={parentRef}
                    onScroll={onScroll}
                    onMouseMove={onMouseMove}
                    onTouchMove={onMouseMove}
                >
                    <div className="flex items-center justify-between">
                        {months.map((month, idx) => {
                            const monthStart = startOfMonth(month.value);
                            const monthEnd = endOfMonth(month.value);
                            const monthMiddle = monthEnd.getTime() - monthStart.getTime();
                            const monthMiddleDate = midDay(new Date(monthStart.getTime() + monthMiddle / 2));

                            // Second block is active if the value.start is before monthMiddle
                            // and value.end is after monthMiddle (or equal for both)
                            let secondActive =
                                value && value.start && value.end
                                    ? monthMiddleDate >= value.start && monthEnd <= value.end
                                    : false;

                            // First block is active if the value.start is before monthMiddle
                            // and value.end is after monthMiddle (or equal for both)
                            let firstActive =
                                value && value.start && value.end
                                    ? monthStart >= value.start && monthMiddleDate <= value.end
                                    : false;

                            const fullMonthActive =
                                value && value.start && value.end
                                    ? monthStart >= value.start && monthEnd <= value.end
                                    : false;

                            if (fullMonthActive) {
                                firstActive = true;
                                secondActive = true;
                            }

                            const isCurrentHovered = hoveredMonth && isSameMonth(hoveredMonth, month.value);

                            const isFirstHalfCurrentActiveStart =
                                value && value.start && !value.end && isSameDay(value.start, month.value);

                            const isSecondHalfCurrentActiveStart =
                                value && value.start && !value.end && isSameDay(value.start, monthMiddleDate);

                            const curIndex = idx * 3;

                            return (
                                <Fragment key={month.label}>
                                    <button
                                        type="button"
                                        data-idx={curIndex + 0}
                                        data-start-ts={monthStart.getTime()}
                                        data-end-ts={monthMiddleDate.getTime()}
                                        className={classNames(
                                            'flex items-center justify-center group/slider-button focus:outline-none',
                                            {
                                                '[&>div]:border-token-stroke-input-hover [&>div]:bg-neutral-200':
                                                    isCurrentHovered || isFirstHalfCurrentActiveStart,

                                                '[&>div]:border-token-stroke-button-default [&>div]:bg-neutral-200':
                                                    isInRange(highlightedRange, curIndex + 0) && !firstActive,
                                            }
                                        )}
                                        onClick={createOnClickHandler(
                                            month.value,
                                            monthMiddleDate,
                                            false,
                                            curIndex + 0
                                        )}
                                        onKeyDown={onKeyDown}
                                    >
                                        <div
                                            className={cn(
                                                indicatorVariants({
                                                    type: 'left',
                                                    isDisabled: month.isDisabled,
                                                    isActive: !!firstActive,
                                                })
                                            )}
                                        />
                                    </button>

                                    <button
                                        type="button"
                                        data-idx={curIndex + 1}
                                        className="h-10 w-2 relative focus:outline-none group/slider-button"
                                        title={`${format(monthMiddleDate, 'MMM yy')}`}
                                        // onClick={createOnClickHandler(
                                        //   month.value,
                                        //   monthEnd,
                                        //   true,
                                        //   curIndex + 1
                                        // )}
                                        // onMouseEnter={() => {
                                        //   setHoveredMonth(month.value);
                                        // }}
                                        // onMouseLeave={() => setHoveredMonth(null)}
                                        // onFocus={() => setHoveredMonth(month.value)}
                                        // onBlur={() => setHoveredMonth(null)}
                                        onKeyDown={onKeyDown}
                                    >
                                        <div className="absolute -bottom-5 w-14 -left-6 select-none">
                                            <Paragraph
                                                mode={mode}
                                                size="xxs"
                                                className="leading-4.5 group-focus/slider-button:text-token-text-light-focused"
                                            >
                                                {month.label}
                                            </Paragraph>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        data-idx={curIndex + 2}
                                        data-start-ts={monthMiddleDate.getTime()}
                                        data-end-ts={monthEnd.getTime()}
                                        className={classNames(
                                            'flex items-center justify-center group/slider-button focus:outline-none mr-4 last:mr-0',
                                            {
                                                '[&>div]:border-token-stroke-input-hover [&>div]:bg-neutral-200':
                                                    isCurrentHovered || isSecondHalfCurrentActiveStart,

                                                '[&>div]:border-token-stroke-button-default [&>div]:bg-neutral-200':
                                                    isInRange(highlightedRange, curIndex + 2) &&
                                                    !secondActive &&
                                                    !isCurrentHovered,

                                                // "[&>div]:border-token-stroke-input-hover [&>div]:bg-neutral-200":
                                                //   isInRange(highlightedRange, curIndex + 2) &&
                                                //   !secondActive &&
                                                //   isCurrentHovered,
                                            }
                                        )}
                                        onClick={createOnClickHandler(monthMiddleDate, monthEnd, false, curIndex + 2)}
                                        onKeyDown={onKeyDown}
                                    >
                                        <div
                                            className={cn(
                                                indicatorVariants({
                                                    type: 'right',
                                                    isDisabled: month.isDisabled,
                                                    isActive: !!secondActive,
                                                })
                                            )}
                                        />
                                    </button>
                                </Fragment>
                            );
                        })}
                    </div>
                </div>

                <div className="flex items-center justify-center pb-5">
                    <button
                        type="button"
                        disabled={!canScrollRight}
                        className={classNames({
                            'cursor-pointer': canScrollRight,
                            'opacity-50': !canScrollRight,
                        })}
                        onClick={() => scrollToOffset(scrollOffset)}
                    >
                        <IconCaretRightFilled size={16} />
                    </button>
                </div>

                {isLoading || disabled ? (
                    <div
                        className={classNames('absolute inset-0', {
                            'cursor-not-allowed': disabled && !isLoading,
                            'cursor-wait': isLoading,
                        })}
                    />
                ) : null}
            </div>
        );
    }
);
PeriodSliderSelector.displayName = 'PeriodSliderSelector';

export const PeriodSelector = ({
    value,
    setValue,

    startDate,
    endDate,
    mode,

    hasError,
    errorMessage,

    isLoading,
    disabled: outerDisabled,

    className,

    name,

    label = 'Select a period',
}: {
    value: DatePeriod | null;
    setValue: (value: DatePeriod | null) => void;

    startDate?: Date;
    endDate?: Date;

    name: string;

    className?: string;

    isLoading?: boolean;
    disabled?: boolean;

    hasError?: boolean;
    errorMessage?: string;

    label?: string;
} & BaseUIComponentProps) => {
    const defaults = useGlobalDefaults(defaultBaseUIComponentProps);
    const theMode = mode ?? defaults.mode;

    const rangeStart = startDate ?? new Date();
    const rangeEnd = endDate ?? addYears(rangeStart, 2);

    const sliderMonths = useSliderMonths(rangeStart, rangeEnd);

    const [exactDates, setExactDates] = useState<boolean>(false);

    const rootRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<SliderSelectorRef>(null);

    const handleOutsideClick = useCallback(() => {
        sliderRef?.current?.handleOutsideClick?.();
    }, []);
    useOutsideClick(rootRef, handleOutsideClick);

    // Make sure that if value is set and it doesn't fit into a slider range exactly,
    // we switch to exact dates mode
    const initialValueHandled = useRef(false);

    useLayoutEffect(() => {
        if (initialValueHandled.current) {
            return;
        }
        initialValueHandled.current = true;

        if (!value) return;
        if (!isDatePeriod(value)) return;

        const startVal = value.start ?? null;
        const endVal = value.end ?? null;

        if (startVal === null && endVal === null) {
            // do nothing, use the slider
            return;
        }

        if (startVal === null || endVal === null) {
            // If we have a value but not both start and end, we switch to exact dates mode
            setExactDates(true);
            return;
        }

        // both start and end date exists

        // this contains all the sensible date chooses for month or half month picks
        const choices = sliderMonths.flatMap((month) => {
            const monthStart = startOfMonth(month.value);
            const monthEnd = endOfMonth(month.value);

            return [monthStart, getMiddleOfMonth(monthStart, monthEnd), monthEnd];
        });

        const startChoiceIndex = choices.findIndex((date) => isSameDay(date, startVal));
        const endChoiceIndex = choices.findIndex((date) => isSameDay(date, endVal));

        if (startChoiceIndex === -1 || endChoiceIndex === -1) {
            // If either start or end is not in the choices, we switch to exact dates mode
            setExactDates(true);
        }
    }, [value, sliderMonths]);

    const disabled = outerDisabled || isLoading;

    return (
        <div className={cn('w-80', className)} ref={rootRef}>
            <div className="grid grid-cols-2 gap-2 pb-4">
                <label className="flex items-center justify-start">
                    <Paragraph
                        className={classNames({
                            '!text-nError-500': hasError,
                        })}
                        variant="title"
                        size="sm"
                        fontWeight="medium"
                        mode={theMode}
                    >
                        {label}
                    </Paragraph>
                </label>

                <div className="flex items-center justify-end gap-2">
                    <Switch
                        checked={exactDates}
                        onCheckedChange={() => {
                            const startVal = value?.start ?? null;
                            const endVal = value?.end ?? null;

                            if (!exactDates) {
                                if (value && startVal && !endVal) {
                                    // If we switch to exact dates and we have a start date but no end date,
                                    // we need to set the end date to the start date
                                    setValue({
                                        start: value.start,
                                        end: value.start,
                                    });
                                }
                            } else if (value && startVal && endVal) {
                                // if we are switching to slider mode, and the value in exact dates mode is not matching
                                // the slider months, we need to reset the value

                                const choices = sliderMonths.flatMap((month) => {
                                    const monthStart = startOfMonth(month.value);
                                    const monthEnd = endOfMonth(month.value);

                                    return [monthStart, getMiddleOfMonth(monthStart, monthEnd), monthEnd];
                                });

                                const startChoiceIndex = choices.findIndex((date) => isSameDay(date, startVal));
                                const endChoiceIndex = choices.findIndex((date) => isSameDay(date, endVal));

                                if (startChoiceIndex === -1 || endChoiceIndex === -1) {
                                    setValue({
                                        start: null,
                                        end: null,
                                    });
                                }
                            }

                            setExactDates((cur) => !cur);
                        }}
                        id={`${name}-exact-dates`}
                        disabled={disabled}
                    />

                    <label htmlFor={`${name}-exact-dates`}>
                        <Paragraph
                            className={classNames('cursor-pointer select-none', {
                                'cursor-not-allowed': disabled,
                            })}
                            mode={theMode}
                        >
                            Exact dates
                        </Paragraph>
                    </label>
                </div>
            </div>

            {exactDates ? (
                <div className="flex gap-2 pb-1">
                    <DatePicker
                        value={value?.start}
                        range={false}
                        onChange={(date) => {
                            const newDate = date ?? value?.start ?? null;

                            let newEndDate = value?.end ?? null;

                            if (newEndDate && newDate) {
                                // make sure end date is after start date
                                if (newDate > newEndDate) {
                                    newEndDate = newDate;
                                }
                            } else if (!newDate && newEndDate) {
                                // reset range if start is not selected
                                newEndDate = null;
                            }

                            setValue({
                                start: newDate !== null ? adjustToMachSliderRanges(new Date(newDate)) : null,
                                end: newEndDate,
                            });
                        }}
                        pickerProps={
                            value?.end
                                ? {
                                      defaultMonth: value?.start ?? value?.end,
                                  }
                                : undefined
                        }
                        mode={theMode}
                        hasError={hasError}
                        placeholder="Select"
                        label="From"
                        wrapperClassName="w-1/2"
                        className="w-full"
                        disabled={disabled}
                    />
                    <DatePicker
                        value={value?.end}
                        range={false}
                        minDate={value?.start ?? undefined}
                        onChange={(date) => {
                            const newDate = date ?? value?.end ?? null;

                            setValue({
                                start: value?.start ?? null,
                                end: newDate !== null ? adjustToMachSliderRanges(new Date(newDate)) : null,
                            });
                        }}
                        disabled={disabled}
                        mode={theMode}
                        hasError={hasError}
                        placeholder="Select"
                        label="To"
                        wrapperClassName="w-1/2"
                        className="w-full"
                        pickerProps={
                            value?.start
                                ? {
                                      defaultMonth: value?.end ?? value?.start,
                                  }
                                : undefined
                        }
                    />
                </div>
            ) : (
                <PeriodSliderSelector
                    months={sliderMonths}
                    value={value}
                    setValue={setValue}
                    mode={theMode}
                    isLoading={isLoading}
                    disabled={disabled}
                    rootRef={rootRef}
                    ref={sliderRef}
                />
            )}

            {hasError && (
                <Paragraph
                    mode={theMode}
                    className={classNames('mt-2 text-xs!', {
                        '!text-nError-500': hasError,
                    })}
                >
                    {errorMessage}
                </Paragraph>
            )}
        </div>
    );
};

const adjustToMachSliderRanges = (date: Date) => {
    const monthEnd = endOfMonth(date);

    // If the date is the end of the month we adjust the time component to the end of the day. This makes sure
    //  that when user switches to non-exact dates mode, the correct range is selected.
    if (isSameDay(date, monthEnd)) {
        return monthEnd;
    }

    // Additionally, lets handle middle of the month dates.
    const monthStart = startOfMonth(date);
    const monthMiddle = getMiddleOfMonth(monthStart, monthEnd);
    if (isSameDay(date, monthMiddle)) {
        return monthMiddle;
    }

    return date;
};

const focusMonth = (parentRef: React.RefObject<HTMLDivElement | null>, direction: number | 'start' | 'end') => {
    if (!parentRef.current) {
        return;
    }

    const wrapper = parentRef.current.children[0];

    if (!wrapper) {
        return;
    }

    if (typeof document === 'undefined') {
        return;
    }

    // Find the current active month
    const activeMonth = document.activeElement;

    if (!activeMonth) {
        return;
    }

    // Find the index of the active month
    const activeIndex = Array.from(wrapper.children).indexOf(activeMonth);

    // Find the new index
    let newIndex = activeIndex;

    if (direction === 'start') {
        newIndex = 0;
    } else if (direction === 'end') {
        newIndex = wrapper.children.length - 1;
    } else {
        newIndex += direction;
    }

    if (newIndex < 0) {
        newIndex = wrapper.children.length - 1;
    } else if (newIndex >= wrapper.children.length) {
        newIndex = 0;
    }

    // Focus the new month
    (wrapper.children[newIndex] as HTMLElement)?.focus?.();
};
